import React from 'react';
import styled, { css } from 'styled-components';
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg';
import { useCallbackRef } from 'hooks/useCallbackRef';
import {
  Table,
  TableCol,
  TableHead,
  TableFoot,
  TableBody,
  TableRow,
  TableCell,
} from 'components/Table';
import { Error } from 'components/Error';
import { Flex } from 'components/Flex';
import { Text } from 'components/Text';
import { Input } from 'components/Input';
import { RadioList, RadioListItem } from 'components/RadioList';
import { Select, SelectItem } from 'components/Select';
import { IconButton } from 'components/IconButton';
import { Label } from './components/Label';
import { FTL_VOLUME, FTL_WEIGHT } from '../shared';

type VolumeType = 'known' | 'unknown';

type VolumeProps = {
  value?: number;
  weight?: number;
  onChange?: (value?: number) => void;
  onWeightChange?: (weight?: number) => void;
  onPalletsChange?: (pallets?: PalletValues) => void;
};

const Volume = (props: VolumeProps) => {
  const {
    value,
    weight,
    onChange = () => {},
    onWeightChange = () => {},
    onPalletsChange = () => {},
  } = props;
  const [volumeType, setVolumeType] = React.useState<VolumeType>();

  function handleVolumeTypeChange(
    event: React.ChangeEvent<React.ElementRef<typeof RadioListItem>>
  ) {
    const radio = event.currentTarget as HTMLInputElement;
    setVolumeType(radio.value as VolumeType);
    onChange(undefined);
    onWeightChange(undefined);
    onPalletsChange(undefined);
  }

  return (
    <>
      <RadioList margin={[0, 0, 'medium']}>
        <RadioListItem
          value="known"
          small
          required
          checked={volumeType === 'known'}
          onChange={handleVolumeTypeChange}
        >
          <Text weight="medium">I know exact volume I need</Text>
        </RadioListItem>
        <RadioListItem
          value="unknown"
          small
          required
          checked={volumeType === 'unknown'}
          onChange={handleVolumeTypeChange}
        >
          <Text weight="medium">I don't know how much volume I need</Text>
        </RadioListItem>
      </RadioList>

      {volumeType === 'known' && (
        <Flex direction="row" spacing={['small', 'small']}>
          <Label text="Loading meters needed">
            <Input
              type="number"
              pattern="[0-9]+"
              min="0"
              max={FTL_VOLUME}
              step="0.1"
              required
              value={value || ''}
              onChange={event => {
                const value = Number(event.target.value);
                onChange(value < FTL_VOLUME ? value : FTL_VOLUME);
              }}
            />
          </Label>
          <Label text="Estimated weight (kg)">
            <Input
              type="number"
              pattern="[0-9]+"
              max={FTL_WEIGHT}
              required
              value={weight || ''}
              onChange={event => {
                const value = Number(event.target.value);
                onWeightChange(value < FTL_WEIGHT ? value : FTL_WEIGHT);
              }}
            />
          </Label>
        </Flex>
      )}

      {volumeType === 'unknown' && (
        <Pallets
          onVolumeChange={onChange}
          onWeightChange={onWeightChange}
          onChange={onPalletsChange}
        />
      )}
    </>
  );
};

type ValueOf<T> = T[keyof T];
type PalletType = 'INDUSTRY' | 'EPAL' | 'CUSTOM';
type PalletConfigs = { [key in PalletType]: Pallet<key> };
export type PalletValues = ValueOf<PalletConfigs>[];

type Pallet<P> = {
  type: P;
  quantity?: number;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
};

const DEFAULT_PALLETS: PalletConfigs = {
  INDUSTRY: {
    quantity: 1,
    type: 'INDUSTRY',
    length: 100,
    width: 120,
    height: 120,
    weight: 50,
  },
  EPAL: {
    quantity: 1,
    type: 'EPAL',
    length: 120,
    width: 80,
    height: 120,
    weight: 50,
  },
  CUSTOM: {
    quantity: 1,
    type: 'CUSTOM',
  },
};

type PalletsProps = {
  onVolumeChange?: (volume: number) => void;
  onWeightChange?: (weight: number) => void;
  onChange?: (pallets: PalletValues) => void;
};

const Pallets = (props: PalletsProps) => {
  const [hasError, setHasError] = React.useState(false);
  const [pallets, setPallets] = React.useState<PalletValues>([
    DEFAULT_PALLETS.INDUSTRY,
  ]);

  const prevVolumeRef = React.useRef<number>();
  const prevWeightRef = React.useRef<number>();
  const onVolumeChange = useCallbackRef(props.onVolumeChange);
  const onWeightChange = useCallbackRef(props.onWeightChange);
  const onChange = useCallbackRef(props.onChange);

  function handlePalletTypeChange(index: number, type: PalletType) {
    setPallets(prevPallets => {
      const nextPallets = [...prevPallets];
      nextPallets[index] = DEFAULT_PALLETS[type];
      return nextPallets;
    });
  }

  function handleValueChange(
    index: number,
    key: keyof Omit<Pallet<PalletType>, 'type'>,
    value: string
  ) {
    const num = parseInt(value, 10);

    setPallets(prevPallets => {
      const nextPallets = [...prevPallets];

      nextPallets[index] = {
        ...nextPallets[index],
        [key]: isNaN(num) ? undefined : num,
        type: 'CUSTOM',
      };

      return nextPallets;
    });
  }

  function handleQuantityChange(index: number, value: string) {
    const num = parseInt(value, 10);

    setPallets(prevPallets => {
      const nextPallets = [...prevPallets];

      nextPallets[index] = {
        ...nextPallets[index],
        quantity: isNaN(num) ? undefined : num,
      };

      return nextPallets;
    });
  }

  function handlePalletAdd() {
    setPallets(prevPallets => [...prevPallets, DEFAULT_PALLETS.INDUSTRY]);
  }

  function handlePalletRemove(index: number) {
    setPallets(prevPallets => {
      return [...prevPallets.slice(0, index), ...prevPallets.slice(index + 1)];
    });
  }

  React.useEffect(() => {
    const volume = pallets.reduce((acc: number, pallet: Pallet<PalletType>) => {
      return hasEveryValue(pallet) ? acc + calculatePalletLdm(pallet) : acc;
    }, 0);

    setHasError(false);

    console.log(volume);

    if (volume >= FTL_VOLUME) {
      onVolumeChange(undefined);
      prevVolumeRef.current = undefined;
      setHasError(true);
    }

    if (prevVolumeRef.current !== volume) {
      onVolumeChange(volume);
      prevVolumeRef.current = volume;
    }
  }, [pallets, onVolumeChange]);

  React.useEffect(() => {
    const weight = pallets.reduce((a, b) => a + (b.weight ?? 0), 0);

    if (prevWeightRef.current !== weight) {
      onWeightChange(weight);
      prevWeightRef.current = weight;
    }
  }, [pallets, onWeightChange]);

  React.useEffect(() => {
    onChange(pallets);
  }, [onChange, pallets]);

  return (
    <>
      {hasError && (
        <Error>
          This quantity is larger than FTL. Contact our sales team to get an
          estimate for this order.
        </Error>
      )}
      <Table>
        <TableCol width={260} />
        <TableCol width={100} />
        <TableCol width={100} />
        <TableCol width={100} />
        <TableCol width={100} />
        <TableCol width={1} />
        <TableHead>
          <TableRow>
            <TableCell header>#</TableCell>
            <TableCell header>
              Length (<abbr title="Centimeters">cm</abbr>)
            </TableCell>
            <TableCell header>
              Width (<abbr title="Centimeters">cm</abbr>)
            </TableCell>
            <TableCell header>
              Height (<abbr title="Centimeters">cm</abbr>)
            </TableCell>
            <TableCell header>
              Weight (<abbr title="Kilograms">kg</abbr>)
            </TableCell>
            <TableCell>&nbsp;</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pallets.map((pallet, index) => {
            const hasRemoveIcon = pallets.length > 1;
            const placeholder = DEFAULT_PALLETS.INDUSTRY;

            return (
              <TableRow key={index}>
                <StyledTableCellSelect>
                  <StyledInput
                    value={String(pallet.quantity ?? '')}
                    placeholder="1"
                    required
                    onChange={event =>
                      handleQuantityChange(index, event.target.value)
                    }
                  />{' '}
                  <StyledSelect
                    value={pallet.type}
                    onChange={(type: PalletType) =>
                      handlePalletTypeChange(index, type)
                    }
                  >
                    <SelectItem value="INDUSTRY">Industry pallet</SelectItem>
                    <SelectItem value="EPAL">EPAL</SelectItem>
                    <SelectItem value="CUSTOM">Custom</SelectItem>
                  </StyledSelect>
                </StyledTableCellSelect>
                <TableCell>
                  <PalletLabel>
                    <StyledInput
                      value={String(pallet.length ?? '')}
                      placeholder={String(placeholder.length ?? '')}
                      required
                      onChange={event =>
                        handleValueChange(index, 'length', event.target.value)
                      }
                    />{' '}
                    <Unit title="Centimeters">cm</Unit>
                  </PalletLabel>
                </TableCell>
                <TableCell>
                  <PalletLabel>
                    <StyledInput
                      value={String(pallet.width ?? '')}
                      placeholder={String(placeholder.width ?? '')}
                      required
                      onChange={event =>
                        handleValueChange(index, 'width', event.target.value)
                      }
                    />{' '}
                    <Unit title="Centimeters">cm</Unit>
                  </PalletLabel>
                </TableCell>
                <TableCell>
                  <PalletLabel>
                    <StyledInput
                      value={String(pallet.height ?? '')}
                      placeholder={String(placeholder.height ?? '')}
                      required
                      onChange={event =>
                        handleValueChange(index, 'height', event.target.value)
                      }
                    />{' '}
                    <Unit title="Centimeters">cm</Unit>
                  </PalletLabel>
                </TableCell>
                <TableCell>
                  <PalletLabel>
                    <StyledInput
                      value={String(pallet.weight ?? '')}
                      placeholder={String(placeholder.weight ?? '')}
                      maxLength={5}
                      required
                      onChange={event =>
                        handleValueChange(index, 'weight', event.target.value)
                      }
                    />{' '}
                    <Unit title="Kilograms">kg</Unit>
                  </PalletLabel>
                </TableCell>
                <TableCell>
                  <IconButton
                    disabled={!hasRemoveIcon}
                    style={
                      !hasRemoveIcon ? { visibility: 'hidden' } : undefined
                    }
                    onClick={() => handlePalletRemove(index)}
                  >
                    <CrossIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
        <TableFoot>
          <TableRow>
            <TableCell colSpan={6}>
              <AddRowButton onClick={handlePalletAdd} />
            </TableCell>
          </TableRow>
        </TableFoot>
      </Table>
    </>
  );
};

function hasEveryValue<T extends PalletType>(
  pallet: Pallet<T>
): pallet is Required<Pallet<T>> {
  return ['length', 'width', 'height', 'weight'].every(key => {
    const palletKey = key as keyof Pallet<T>;
    return pallet[palletKey] !== undefined;
  });
}

function calculatePalletLdm({
  quantity = 1,
  length,
  width,
  height,
}: Required<Pallet<PalletType>>) {
  const option1 = calculateOptionOneLDM(length, width);
  const option2 = calculateOptionTwoLDM(length, width, height);

  return quantity * Math.max(option1, option2);
}

function calculateOptionOneLDM(length: number, width: number) {
  return (cmToMeters(length) * cmToMeters(width)) / 2.4;
}

function calculateOptionTwoLDM(length: number, width: number, height: number) {
  return (cmToMeters(length) * cmToMeters(width) * cmToMeters(height)) / 5;
}

function cmToMeters(value: number) {
  return value / 100;
}

const fieldStyles = css`
  padding: ${props => props.theme.spacing.tiny};
  height: auto;
  color: inherit;
`;

const StyledTableCellSelect = styled(TableCell)`
  white-space: nowrap;
`;

const StyledSelect = styled(Select)`
  ${fieldStyles};
`;

const PalletLabel = styled.label`
  white-space: nowrap;
  flex: 1 1 auto;
`;

const StyledInput = styled(Input).attrs(props => ({
  type: 'tel',
  autosize: true,
  pattern: '[0-9]+',
  maxLength: props.maxLength || 3,
}))`
  ${fieldStyles};
  padding-left: 2px;
  padding-right: 2px;
  min-width: 1em;
  margin-left: -4px;
`;

const Unit = styled(Text).attrs(() => ({
  as: 'abbr',
}))``;

const AddRowButton = styled(Text).attrs(() => ({
  as: 'button',
  type: 'button',
  font: 'small',
  color: 'textSubtle',
  children: 'Add new',
}))`
  background: transparent;
  border: 0;
  width: 100%;
  height: 100%;
  outline: none;
  transition: color 166ms ease-out;

  &:hover,
  &.focus-visible:focus {
    color: inherit;
  }
`;

export { Volume };
